export type ProposalAttachment = {
  attachmentId: string
  filename: string
  name: string
  size: number
  sortOrder?: number
}

export const maxAttachmentCount = 10
export const maxTotalAttachmentBytes = 100 * 1024 * 1024
export const maxSingleAttachmentBytes = 50 * 1024 * 1024

export const blockedExtensions = [
  'exe', 'bat', 'cmd', 'com', 'msi', 'scr', 'ps1', 'sh', 'app', 'jar', 'dll', 'vbs'
]

export const isBlockedFile = (filename: string): boolean =>
  blockedExtensions.includes(fileExtensionLabel(filename).toLowerCase())

export const rejectionReason = (file: { name: string, size: number }): string | undefined => {
  if (isBlockedFile(file.name)) {
    return `${file.name} is an executable type and cannot be attached to a proposal.`
  }
  if (file.size > maxSingleAttachmentBytes) {
    return `${file.name} is larger than the ${maxSingleAttachmentBytes / (1024 * 1024)} MB per-file limit.`
  }
  return undefined
}

export const fileExtensionLabel = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.')
  if (lastDot < 1 || lastDot === filename.length - 1) {
    return 'FILE'
  }
  return filename.slice(lastDot + 1).toUpperCase()
}

export const fileSizeLabel = (bytes: number): string => {
  if (bytes < 0) {
    return '0 KB'
  }
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const attachmentDisplayName = (attachment: ProposalAttachment): string => {
  const extension = fileExtensionLabel(attachment.filename)
  if (!attachment.name) {
    return attachment.filename
  }
  if (extension === 'FILE' || attachment.name.toUpperCase().endsWith(`.${extension}`)) {
    return attachment.name
  }
  return `${attachment.name}.${extension.toLowerCase()}`
}

export const attachmentSubtitle = (attachment: ProposalAttachment): string =>
  `${fileSizeLabel(attachment.size)} • ${fileExtensionLabel(attachment.filename)}`

export const totalAttachmentBytes = (attachments: ProposalAttachment[]): number =>
  attachments.reduce((sum, x) => sum + x.size, 0)

export const attachmentErrors = (attachments: ProposalAttachment[]): string[] => {
  const errors: string[] = []
  if (attachments.length > maxAttachmentCount) {
    errors.push(`A proposal can have at most ${maxAttachmentCount} attachments.`)
  }
  if (totalAttachmentBytes(attachments) > maxTotalAttachmentBytes) {
    errors.push(`Proposal attachments cannot exceed ${maxTotalAttachmentBytes / (1024 * 1024)} MB in total.`)
  }
  return errors
}

export const canAcceptMore = (attachments: ProposalAttachment[], incomingCount: number): boolean =>
  attachments.length + incomingCount <= maxAttachmentCount

export const removeAttachment = (attachments: ProposalAttachment[], attachmentId: string): ProposalAttachment[] =>
  attachments.filter(x => x.attachmentId !== attachmentId)

export const appendAttachment = (attachments: ProposalAttachment[], added: ProposalAttachment): ProposalAttachment[] =>
  attachments.some(x => x.attachmentId === added.attachmentId)
    ? attachments
    : [...attachments, added]

export const formatEstimatedHours = (hours: number | undefined): string => {
  if (hours === undefined || hours === null) {
    return '—'
  }
  const rounded = Math.round(hours * 10) / 10
  return Number.isInteger(rounded) ? `${rounded}h` : `${rounded.toFixed(1)}h`
}
